import { Chart as G2Chart } from '@antv/g2';
import { forwardRef, HTMLAttributes, Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { IBaseChartProps, IComponent } from '../../../models/components.models';
import { useClassNames } from '../../../utils/hooks';
import { funnelChartStylesDefault, IFunnelChartClassNames, IFunnelChartStyles } from './FunnelChart.styles';

const render = (el: HTMLElement, data: { [key: string]: any }[], labelFieldName: string, valueFieldNames: string[]) => {
  const chart = new G2Chart({ container: el, autoFit: true });
  chart.coordinate({
    transform: [{ type: 'transpose' }],
  });
  chart
    .interval()
    .data(data)
    .encode('x', labelFieldName)
    .encode('y', valueFieldNames[0])
    .encode('color', labelFieldName)
    .encode('shape', 'funnel')
    .transform({ type: 'symmetryY' })
    .scale('x', { padding: 0 })
    .animate('enter', { type: 'fadeIn' })
    .label({
      text: (d: any) => `${d[labelFieldName]}\n${d[valueFieldNames[0]]}`,
      position: 'inside',
      transform: [{ type: 'contrastReverse' }],
    })
    .axis(false);
  chart.render();
};

export interface IFunnelChartProps
  extends IComponent<IFunnelChartStyles>,
    HTMLAttributes<HTMLDivElement>,
    IBaseChartProps {}

export const FunnelChart = forwardRef((props: IFunnelChartProps, ref: Ref<HTMLDivElement>) => {
  // some variables are not used, but they need to get `elementProps`.
  const { className, styles, data = [], labelFieldName = '', valueFieldNames = [], ...elementProps } = props;
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  useEffect(() => {
    if (refRoot.current) {
      render(refRoot.current, data, labelFieldName, valueFieldNames);
    }
  }, [data, labelFieldName, valueFieldNames]);

  const classNames = useClassNames<Partial<IFunnelChartStyles>, IFunnelChartClassNames>(
    'FunnelChart',
    funnelChartStylesDefault,
    styles,
    className
  );
  return <div {...elementProps} ref={refRoot} className={classNames.root} />;
});
